import { useCallback, useEffect, useState } from 'react';
import { ArrowRight, Download, Eye, FileScan } from 'lucide-react';
import { formatDateTime } from '@nu/shared';
import { api } from '../api/client.js';
import { problem } from '../modules/bookings/util.js';
import Modal from './Modal.jsx';

const LEVEL = { stop: 'Stop', check: 'Check', note: 'Note' };

// A job's risk assessment photos (children) with, to their right after an
// arrow, what can be done with them (office): "Digitise risk assessment"
// reads both pages into a digital copy (about a minute; the server does it),
// then "View report" shows it here and "Download" saves the PDF; what to
// check is listed under them. Until both pages are on the job, just the
// photos. A retake after it was read offers to read it again.
// See api/src/modules/riskAssessments.
export default function DigitalRiskAssessment({ bookingId, reference = '', children }) {
  const base = `/bookings/${bookingId}/risk-assessment`;
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [viewing, setViewing] = useState(null); // the report's blob URL
  const load = useCallback(() => api(base).then(setState, () => {}), [base]);
  useEffect(() => { load(); }, [load]);
  const reading = state?.reading;
  // While it's being read, look again every few seconds.
  useEffect(() => {
    if (reading?.status !== 'reading') return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [reading?.status, load]);
  useEffect(() => () => { if (viewing) URL.revokeObjectURL(viewing); }, [viewing]);

  if (!state || !state.pages.front || !state.pages.back) return children;
  const name = `Risk assessment ${reference || bookingId}.pdf`;
  const digitise = async () => {
    setBusy(true);
    setError(null);
    try {
      setState({ ...state, ...(await api(base, { method: 'POST' })) });
    } catch (e) {
      setError(problem(e));
    } finally {
      setBusy(false);
    }
  };
  const report = () => api(`${base}/report.pdf`, { as: 'blob' });
  const view = async () => {
    setError(null);
    try { setViewing(URL.createObjectURL(await report())); } catch (e) { setError(problem(e)); }
  };
  const download = async () => {
    setError(null);
    try {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(await report());
      a.download = name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
    } catch (e) {
      setError(problem(e));
    }
  };

  const done = reading?.status === 'done';
  const fresh = done && reading.upToDate;
  return (
    <>
      <div className="ra-row">
        {children}
        <ArrowRight size={22} aria-hidden="true" className="ra-arrow" />
        <div className="ra-actions">
          {state.configured && reading?.status !== 'reading' && !fresh && (
            <button type="button" className="btn primary with-icon" disabled={busy} onClick={digitise}>
              <FileScan size={16} aria-hidden="true" /> {done ? 'Read it again' : 'Digitise risk assessment'}
            </button>
          )}
          {reading?.status === 'reading' && <p className="small"><strong>Reading the form…</strong> About a minute.</p>}
          {done && (
            <>
              <button type="button" className={`btn ra-tile${fresh ? ' primary' : ''}`} onClick={view}>
                <Eye size={22} aria-hidden="true" /> View report
              </button>
              <button type="button" className="btn ra-tile" onClick={download}>
                <Download size={22} aria-hidden="true" /> Download
              </button>
            </>
          )}
          {!state.configured && !done && <p className="small muted">Digitising isn’t set up on this server yet.</p>}
        </div>
      </div>
      <div className="ra-digital">
        {reading?.status === 'failed' && <p className="notice-line notice-bad">{reading.error || 'The reading failed.'}</p>}
        {done && !reading.upToDate && <p className="small warn-text">The photos have changed since it was read.</p>}
        {done && (
          <>
            <p className="small muted">Read {formatDateTime(reading.finishedAt)}{reading.requestedBy ? ` for ${reading.requestedBy}` : ''}. Read from the photos by AI: the photos are the record.</p>
            {reading.checks.length
              ? (
                <ul className="ra-checks">
                  {reading.checks.map((c, i) => (
                    <li key={i} className={`ra-check ra-check-${c.level}`}><span className="ra-check-tag">{LEVEL[c.level]}</span> {c.text}</li>
                  ))}
                </ul>
              )
              : <p className="small">Nothing to flag.</p>}
          </>
        )}
        {error && <p className="notice-line notice-bad">{error}</p>}
      </div>
      <Modal
        open={Boolean(viewing)}
        wide
        title={name.replace(/\.pdf$/, '')}
        onClose={() => setViewing(null)}
        footer={(
          <>
            <button type="button" className="btn with-icon" onClick={download}><Download size={16} aria-hidden="true" /> Download</button>
            <button type="button" className="btn" onClick={() => setViewing(null)}>Close</button>
          </>
        )}
      >
        {viewing && <iframe className="ra-report-frame" src={viewing} title={name} />}
      </Modal>
    </>
  );
}
